import React, { useContext, useState } from "react";
import {
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { NavLink } from "react-router-dom";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined"; // Example icon for 'Customers'
import EventIcon from "@mui/icons-material/Event"; // Example icon for 'Event management'
import ContentPasteOutlinedIcon from "@mui/icons-material/ContentPasteOutlined"; // Example icon for 'Content management'
import BookOutlinedIcon from "@mui/icons-material/BookOutlined"; // Example icon for 'Booking management'
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount"; // Example icon for 'Organiser management'
import SupportAgentIcon from "@mui/icons-material/SupportAgent"; // Example icon for 'Communication & Support'
import BarChartIcon from "@mui/icons-material/BarChart"; // Example icon for 'Reporting'
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import SettingsIcon from "@mui/icons-material/Settings"; // Example icon for 'Settings'
import { EnvironmentContext } from "../EnvironmentContext";
import ButtonCompo from "./ButtonComponent/ButtonCompo";
import APIServices from "../services/APIServices";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAccessToken, setRefreshToken, setUserDetails, setEventDetails, setLoginUserDetails } from "../Redux/Actions";
import { Backdrop, CircularProgress } from '@mui/material';

const drawerWidth = 240;

function MenuAppBar({ organiserManagementLength, bookingManagementLength }) {
  const dispatch = useDispatch()
  const theme = useTheme();
  const navigate = useNavigate()
  const loggedUserDetails = useSelector((state) => state.loginUserDetails)
  const { environment, setEnvironment } = useContext(EnvironmentContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false)
  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogoutAccount = () => {
    const confirmed = window.confirm("Are you sure you want to logout ?");
    if (!confirmed) return;
    setShowLoader(true);

    const body = {
      email: loggedUserDetails?.mail,
    };

    APIServices.logout(body)
      .then((res) => {
        localStorage.clear();
        dispatch(setAccessToken(null));
        dispatch(setRefreshToken(null));
        dispatch(setUserDetails(null));
        dispatch(setEventDetails(null));
        navigate("/login");
      })
      .catch((err) => {
        console.log("Error during logout:", err);
        // Ensure local logout even if API call fails
        localStorage.clear();
        dispatch(setAccessToken(null));
        dispatch(setRefreshToken(null));
        dispatch(setUserDetails(null));
        dispatch(setEventDetails(null));
        navigate("/login");
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const sideNavbarItems = [
    { icon: <PeopleOutlinedIcon />, text: "Customers", path: "/customer" },
    {
      icon: <EventIcon />,
      text: "Event Management",
      path: "/event-management",
    },
    {
      icon: <ContentPasteOutlinedIcon />,
      text: "Content Management",
      path: "/content-management",
    },
    {
      icon: <BookOutlinedIcon />,
      text: `Booking management${bookingManagementLength > 0 ? ` (${bookingManagementLength})` : ""
        }`,
      path: "/booking-management",
    },
    {
      icon: <SupportAgentIcon />,
      text: `Organiser management${organiserManagementLength > 0 ? ` (${organiserManagementLength})` : ""
        }`,
      path: "/organiser-management",
    },
    {
      icon: <BarChartIcon />,
      text: "Reporting",
      path: "/reporting"
    },
    {
      icon: <FeedbackOutlinedIcon />,
      text: "Feedback",
      path: "/allfeedbacks"
    },
  ];

  const drawer = (
    <div>
      {isLoggedIn && (
        <>
          <Toolbar />
          <List>
            {sideNavbarItems.map((item, index) => (
              <ListItem
                button
                component={NavLink}
                to={item.path}
                key={item.text}
                style={({ isActive }) => ({
                  color: isActive ? "#EE8E8E" : "inherit",
                })}
                onClick={isMobile ? handleDrawerToggle : null}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <ListItemText primary={item.icon} />
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      style: { fontFamily: "Poppins", fontSize: "14px" },
                    }}
                  />
                </div>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex" }}>
      <Backdrop
        sx={{
          color: "#ffffff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CssBaseline />
      <AppBar
        position="fixed"
        style={{ zIndex: theme.zIndex.drawer + 1 }}
        sx={{ backgroundColor: "#FFFFFF" }}
      >
        <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }}>
          {isMobile && (
            <IconButton
              sx={{ color: "grey" }}
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Toolbar className="header-bg-logo-container">
            <img
              src="/Logo.svg"
              alt="Logo"
              className="logo"
              style={{ cursor: "pointer" }}
            />
            <div style={{ display: 'flex', flexDirection: 'row' }}>

              <Typography
                style={{ color: "black" }}
                sx={{
                  display: { xs: "none", md: "flex" },
                  mr: 1,
                  mt: 0.6,
                  fontWeight: "700",
                  fontFamily: "poppins",
                }}
              >
                Environment
              </Typography>

              <Select
                className="select-environment"
                required
                size="small"
                style={{
                  fontFamily: "poppins",
                  fontSize: "13px",
                  fontWeight: "500",
                  borderRadius: "12px",
                  width: "100%",
                  height: "34px",
                  color: "#059669",
                }}
                onChange={(e) => setEnvironment(e.target.value)}
                labelId="demo-controlled-open-select-label"
                id="demo-controlled-open-select"
                value={environment}
                renderValue={(selected) => {
                  if (selected.length === 0) {
                    return <span>Environment</span>;
                  }
                  return selected;
                }}
              >
                <MenuItem
                  selected
                  value="Production"
                  style={{
                    fontFamily: "poppins",
                    fontSize: "13px",
                  }}
                >
                  Production
                </MenuItem>
                <MenuItem
                  value="Testing"
                  style={{
                    fontFamily: "poppins",
                    fontSize: "13px",
                  }}
                >
                  Testing
                </MenuItem>
              </Select>

            </div>
          </Toolbar>
          {isLoggedIn ? (
            <ButtonCompo btnText="Sign out" btnOutlined onClick={handleLogoutAccount} />
          ) : null}
        </Toolbar>
      </AppBar>
      <nav>
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            style={{ width: drawerWidth }}
          >
            {drawer}
          </Drawer>
        )}
        {!isMobile && (
          <Drawer variant="permanent" open style={{ width: drawerWidth }}>
            {drawer}
          </Drawer>
        )}
      </nav>
    </div>
  );
}

export default MenuAppBar;
